import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Pane,
  Heading,
  Text,
  Table,
  Spinner,
  TabNavigation,
  Tab,
  Link
} from 'evergreen-ui';
import { GET_INACTIVE_USERS } from '../../../graphql/queries/Auth/QueryAuth';

/**
 * InactiveUsersBox component displays users who haven't logged in for a specified period
 * with the ability to view publisher details
 */
const InactiveUsersBox = ({ onSelectPublisher }) => {
  // State for period selection
  const [selectedPeriod, setSelectedPeriod] = useState('week');

  // Map UI periods to actual query parameter values
  const periodMap = {
    'week': 'OVER_1_WEEK',
    'month': 'OVER_1_MONTH',
    'quarter': 'OVER_3_MONTHS'
  };

  // Get inactive users based on selected period
  const { data, loading, error } = useQuery(GET_INACTIVE_USERS, {
    variables: { period: periodMap[selectedPeriod] },
    fetchPolicy: 'network-only'
  });

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  // Helper function to get user display name
  const getUserDisplayName = (user) => {
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }
    return user.username || user.email;
  };

  // Filter users to exclude workinglit.com, ideacog.net emails, and superusers
  const filterUsers = (users) => {
    return users.filter(user => {
      // Skip users with workinglit.com or ideacog.net email domains
      if (user.email &&
         (user.email.includes('@workinglit.com') ||
          user.email.includes('@ideacog.net'))) {
        return false;
      }

      // Skip superusers
      if (user.isSuperuser) {
        return false;
      }

      return true;
    });
  };

  // Function to get the heading text based on selected period
  const getPeriodHeadingText = () => {
    switch(selectedPeriod) {
      case 'week':
        return 'Users Inactive for 1+ Week';
      case 'month':
        return 'Users Inactive for 1+ Month';
      case 'quarter':
        return 'Users Inactive for 3+ Months';
      default:
        return 'Inactive Users';
    }
  };

  if (loading) {
    return (
      <Pane display="flex" alignItems="center" justifyContent="center" height={200}>
        <Spinner />
      </Pane>
    );
  }

  if (error) {
    return (
      <Pane background="redTint" padding={16} borderRadius={4}>
        <Text color="red500">Error loading inactive users: {error.message}</Text>
      </Pane>
    );
  }

  const inactiveUsers = data?.inactiveUsers || [];
  const filteredInactiveUsers = filterUsers(inactiveUsers);

  return (
    <Pane background="tint1" padding={16} borderRadius={4} marginBottom={24}>
      <Pane display="flex" justifyContent="space-between" alignItems="center" marginBottom={16}>
        <Heading size={500}>{getPeriodHeadingText()}</Heading>
        <TabNavigation>
          <Tab
            id="week"
            isSelected={selectedPeriod === 'week'}
            onSelect={() => setSelectedPeriod('week')}
          >
            Over 1 Week
          </Tab>
          <Tab
            id="month"
            isSelected={selectedPeriod === 'month'}
            onSelect={() => setSelectedPeriod('month')}
          >
            Over 1 Month
          </Tab>
          <Tab
            id="quarter"
            isSelected={selectedPeriod === 'quarter'}
            onSelect={() => setSelectedPeriod('quarter')}
          >
            Over 3 Months
          </Tab>
        </TabNavigation>
      </Pane>

      {filteredInactiveUsers.length === 0 ? (
        <Text>No inactive users found for this period.</Text>
      ) : (
        <Table>
          <Table.Head>
            <Table.TextHeaderCell>User</Table.TextHeaderCell>
            <Table.TextHeaderCell>Email</Table.TextHeaderCell>
            <Table.TextHeaderCell>Publisher</Table.TextHeaderCell>
            <Table.TextHeaderCell>Last Login</Table.TextHeaderCell>
          </Table.Head>
          <Table.Body>
            {filteredInactiveUsers.map(user => (
              <Table.Row key={user.id}>
                <Table.TextCell>{getUserDisplayName(user)}</Table.TextCell>
                <Table.TextCell>{user.email}</Table.TextCell>
                <Table.TextCell>
                  {user.publisheruserSet && user.publisheruserSet[0] ? (
                    <Link
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        onSelectPublisher && onSelectPublisher(user.publisheruserSet[0].publisher);
                      }}
                    >
                      {user.publisheruserSet[0].publisher.name}
                    </Link>
                  ) : (
                    'N/A'
                  )}
                </Table.TextCell>
                <Table.TextCell>{formatDate(user.lastLogin)}</Table.TextCell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </Pane>
  );
};

export default InactiveUsersBox;
